import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Avatar, Dropdown, Menu } from 'antd';
import { TbDatabase, TbDatabaseSearch, TbWorld, TbZoomInArea, TbZoomMoney } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    DropdownMenuLabel,
    DropdownPanel,
    DropdownPanelMenu,
    EmptySection,
    EnrichedAnchorTag,
    LearnMoreFooter,
} from '@/shared';
import { userFacingFeatures } from '@/shared/constants';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';
import { DataSourceType, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import SelectOptionContent from '../../../../components/SelectOptionContent';

interface DatasetSelectionDropdownProps {
    component: ReportComponent;
    children?: ReactNode;
}

const DatasetSelectionDropdown: FC<DatasetSelectionDropdownProps> = ({ component, children }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [search, setSearch] = useState('');

    const [width, setWidth] = useState<number>();
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, []);

    const options = [
        {
            key: DataSourceType.PLATFORM_FINANCIAL,
            icon: <TbZoomMoney style={{ color: token.colorTextTertiary }} size='75%' />,
            label: <>Financial Data</>,
            searchText: 'financial data corporate financials earnings historicals FMP',
            description: (
                <>
                    Institutional-grade corporate financials, earnings, and historicals powered by{' '}
                    <EnrichedAnchorTag url='https://financialmodelingprep.com' label={'FMP'} />
                </>
            ),
        },
        {
            key: DataSourceType.PLATFORM_ECONOMIC,
            icon: <TbWorld style={{ color: token.colorTextTertiary }} size='75%' />,
            label: <>Economic Data</>,
            searchText:
                'economic data commodities currencies cryptocurrencies crypto treasury rates indicators forex esg macro FMP',
            description: (
                <>
                    Market, macroeconomic, treasury, currency, digital asset, and ESG datasets powered by{' '}
                    <EnrichedAnchorTag url='https://financialmodelingprep.com' label='FMP' />
                </>
            ),
        },
        {
            key: DataSourceType.GLOBAL_TRADE,
            icon: <TbWorld style={{ color: token.colorTextTertiary }} size='75%' />,
            label: <>Global Trade</>,
            searchText: 'global trade logistics weather commodities mobility foot traffic machinery imports spatial',
            description: (
                <>Governed spatial and operational datasets across the physical-world capital-markets value chain.</>
            ),
        },
        {
            key: DataSourceType.WORKSPACE_ANALYTICS,
            icon: <TbZoomInArea style={{ color: token.colorTextTertiary }} size='75%' />,
            label: <span>Workspace Data</span>,
            searchText: 'workspace data internal research resources team workflows',
            description: <span>Internal workspace data across research activity, resources, and team workflows.</span>,
        },
        {
            key: DataSourceType.THIRD_PARTY,
            icon: <TbDatabaseSearch style={{ color: token.colorTextTertiary }} size='75%' />,
            label: <>Custom Data Sources</>,
            searchText: 'custom data sources proprietary external third party',
            description: <>Connect proprietary datasets and external providers via third-party Data Sources.</>,
        },
    ];
    const availableOptions = options.filter(
        (option) => userFacingFeatures.globalTradeSearch || option.key !== DataSourceType.GLOBAL_TRADE,
    );
    const normalizedSearch = search.trim().toLocaleLowerCase();
    const filteredOptions = normalizedSearch
        ? availableOptions.filter((option) => option.searchText.includes(normalizedSearch))
        : availableOptions;

    const replaceDraft = useReportComponentStore((s) => s.replaceDraftConfiguration);
    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);

    const draft = draftConfigurations[component?.id as string];

    return (
        <Dropdown
            trigger={['click']}
            overlayStyle={{ width }}
            popupRender={() => (
                <DropdownPanel
                    footer={<LearnMoreFooter topic='our data' variant='dropdown' />}
                    search={{
                        placeholder: 'Filter Datasets...',
                        setValue: setSearch,
                        value: search,
                    }}
                    style={{ marginTop: '0.25rem' }}
                    width={width}
                >
                    <DropdownMenuLabel>
                        {filteredOptions.length} Option{filteredOptions.length === 1 ? '' : 's'}
                    </DropdownMenuLabel>
                    <DropdownPanelMenu selectable={false}>
                        {filteredOptions.length !== 0 ? (
                            filteredOptions.map((o) => {
                                return (
                                    <Menu.Item
                                        key={o?.key}
                                        onClick={() => {
                                            if (draft?.dataSet?.type === o.key) {
                                                return;
                                            }

                                            const baseDraft = (draft ??
                                                component.configuration ??
                                                {}) as Partial<ReportComponentConfig>;

                                            replaceDraft(component.id, {
                                                ...baseDraft,
                                                data: undefined,
                                                dataSet: {
                                                    type: o?.key,
                                                },
                                                query: {
                                                    datasetId: '',
                                                    metrics: [],
                                                    dimensions: [],
                                                },
                                            });
                                        }}
                                    >
                                        <SelectOptionContent
                                            icon={
                                                <Avatar
                                                    size={16}
                                                    icon={o?.icon}
                                                    shape='square'
                                                    style={{ border: `1px solid ${token.colorBorder}` }}
                                                />
                                            }
                                            title={o?.label}
                                            description={o?.description}
                                            selected={draft?.dataSet?.type === o?.key}
                                        />
                                    </Menu.Item>
                                );
                            })
                        ) : (
                            <EmptySection
                                height='12.5rem'
                                icon={<TbDatabase size='75%' style={{ color: token.colorTextTertiary }} />}
                                label='No Dataset Options'
                                description={
                                    search
                                        ? 'No dataset options match your search.'
                                        : "You don't have any dataset options to select."
                                }
                            />
                        )}
                    </DropdownPanelMenu>
                </DropdownPanel>
            )}
        >
            <div ref={ref}>
                <span>{children}</span>
            </div>
        </Dropdown>
    );
};

export default DatasetSelectionDropdown;
